import React from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { TableCell, TableRow } from '@material-ui/core';
import { ColumnDef } from './types';

const ROW_HEIGHT = 52;
const OVERSCAN = 8;

const CELL_STYLE: React.CSSProperties = {
    padding: '6px 12px',
    borderRight: '1px solid #e0e0e0',
    borderBottom: '1px solid #e0e0e0',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
};

export interface DataTableBodyProps<T> {
    rows: T[];
    columns: ColumnDef<T>[];
    getRowId: (row: T) => string | number;
    parentRef: React.RefObject<HTMLDivElement | null>;
}

function DataTableBody<T>({ rows, columns, getRowId, parentRef }: DataTableBodyProps<T>) {
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: OVERSCAN,
        getItemKey: (index) => getRowId(rows[index]) ?? index,
    });

    const virtualRows = rowVirtualizer.getVirtualItems();

    return (
        <div
            style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
            }}
        >
            {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                if (!row) return null;
                return (
                    <TableRow
                        key={getRowId(row)}
                        component="div"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: `${virtualRow.size}px`,
                            transform: `translateY(${virtualRow.start}px)`,
                            display: 'flex',
                            margin: 0,
                            padding: 0,
                            boxSizing: 'border-box',
                        }}
                    >
                        {columns.map((col) => {
                            const flex = col.flex ?? 1;
                            return (
                                <TableCell
                                    key={col.id}
                                    component="div"
                                    padding="none"
                                    style={{
                                        ...CELL_STYLE,
                                        flex: flex === 0 ? `0 0 ${col.width}px` : `${flex} 1 ${col.width}px`,
                                        width: col.width,
                                        minWidth: col.minWidth ?? col.width,
                                        justifyContent:
                                            col.align === 'right'
                                                ? 'flex-end'
                                                : col.align === 'center'
                                                ? 'center'
                                                : 'flex-start',
                                    }}
                                >
                                    {col.render(row)}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                );
            })}
        </div>
    );
}

export default React.memo(DataTableBody) as typeof DataTableBody;
